import React, { useEffect, useMemo, useState } from 'react';
import { TraceEvent } from '../../types';
import {
    Box,
    Typography,
    Collapse,
    IconButton,
    Chip,
    Paper,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Memory as CpuIcon,
    Storage as DatabaseIcon,
    TipsAndUpdates as LightbulbIcon,
    Error as AlertCircleIcon,
    Search as SearchIcon,
    Timeline as ActivityIcon,
    Bolt as ZapIcon,
    Place as MapPinIcon,
    TrendingUp as TrendingUpIcon,
    Warning as AlertTriangleIcon,
    Info as InfoIcon,
} from '@mui/icons-material';

interface AgentThinkingDetailsProps {
    traces: TraceEvent[];
    isComplete: boolean;
    excludeNodes?: string[];
    includeNodes?: string[];  // When set, ONLY show these nodes (takes precedence over excludeNodes)
}

// Region configuration for badges
const REGION_CONFIG: Record<string, { bg: string; color: string; borderColor: string }> = {
    'GULF': { bg: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' },
    'LOS_ANGELES': { bg: '#e0f2fe', color: '#0369a1', borderColor: '#7dd3fc' },
    'CHICAGO': { bg: '#fce7f3', color: '#9d174d', borderColor: '#f9a8d4' },
    'NATIONAL': { bg: '#e0e7ff', color: '#4338ca', borderColor: '#a5b4fc' },
};

// Severity detection for shock content
const getSeverity = (content: string): 'critical' | 'warning' | 'info' => {
    const lower = content.toLowerCase();
    if (lower.includes('critical') || lower.includes('cancel') || lower.includes('surge') || lower.includes('aged')) return 'critical';
    if (lower.includes('opportunity') || lower.includes('drop') || lower.includes('aggressive')) return 'warning';
    return 'info';
};

const SEVERITY_COLORS = {
    critical: { border: '#f87171', bg: 'rgba(254, 226, 226, 0.5)' },
    warning: { border: '#fbbf24', bg: 'rgba(254, 243, 199, 0.5)' },
    info: { border: '#e2e8f0', bg: 'rgba(248, 250, 252, 0.5)' },
};

export const AgentThinkingDetails: React.FC<AgentThinkingDetailsProps> = ({
    traces,
    isComplete,
    excludeNodes = [],
    includeNodes
}) => {
    // Filter traces: includeNodes takes precedence, otherwise use excludeNodes
    const filteredTraces = traces.filter(t => {
        const node = t.node || 'system';
        if (includeNodes && includeNodes.length > 0) {
            return includeNodes.includes(node);
        }
        return !excludeNodes.includes(node);
    });

    const [visibleCount, setVisibleCount] = useState(0);
    const [expandedShocks, setExpandedShocks] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (isComplete) {
            setVisibleCount(filteredTraces.length);
            return;
        }

        if (filteredTraces.length === 0) {
            setVisibleCount(0);
            return;
        }

        if (visibleCount > filteredTraces.length) {
            setVisibleCount(filteredTraces.length);
            return;
        }

        if (visibleCount >= filteredTraces.length) {
            return;
        }

        const timer = window.setTimeout(() => {
            setVisibleCount((prev) => Math.min(filteredTraces.length, prev + 1));
        }, 180);

        return () => window.clearTimeout(timer);
    }, [filteredTraces.length, isComplete, visibleCount]);

    const visibleTraces = filteredTraces.slice(0, visibleCount);

    // Group traces by node
    const groupedTraces = visibleTraces.reduce((acc, trace) => {
        const node = trace.node || 'system';
        if (!acc[node]) acc[node] = [];
        acc[node].push(trace);
        return acc;
    }, {} as Record<string, TraceEvent[]>);

    const nodes = Object.keys(groupedTraces);
    const latestNode = useMemo(() => nodes[nodes.length - 1], [nodes]);
    const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>(() => {
        if (!latestNode) return {};
        return { [latestNode]: true };
    });

    useEffect(() => {
        if (!latestNode) return;
        setExpandedNodes((prev) => ({ ...prev, [latestNode]: true }));
    }, [latestNode]);

    useEffect(() => {
        if (visibleTraces.length === 0) {
            setExpandedShocks({});
        }
    }, [visibleTraces.length]);

    if (visibleTraces.length === 0) return null;

    const toggleNode = (node: string) => {
        setExpandedNodes(prev => ({ ...prev, [node]: !prev[node] }));
    };

    const toggleShock = (key: string) => {
        setExpandedShocks(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getIcon = (type: string, message: string) => {
        const msg = message.toLowerCase();
        const iconSx = { fontSize: 16 };
        if (msg.includes('mcp') || msg.includes('intelligence') || msg.includes('collecting')) return <SearchIcon sx={{ ...iconSx, color: '#14b8a6' }} />;
        if (msg.includes('synthesized') || msg.includes('market shocks')) return <DatabaseIcon sx={{ ...iconSx, color: '#6366f1' }} />;
        if (msg.includes('formulating') || msg.includes('recipes') || msg.includes('strategic')) return <LightbulbIcon sx={{ ...iconSx, color: '#f59e0b' }} />;
        if (msg.includes('check_') || msg.includes('validation') || msg.includes('regional')) return <ActivityIcon sx={{ ...iconSx, color: '#8b5cf6' }} />;
        if (type === 'error') return <AlertCircleIcon sx={{ ...iconSx, color: '#ef4444' }} />;
        return <CpuIcon sx={{ ...iconSx, color: '#94a3b8' }} />;
    };

    const formatNodeName = (node: string) => {
        return node.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: '#fff',
            }}
        >
            {/* Main Header */}
            <Box
                sx={{
                    px: 2.5,
                    py: 1.5,
                    background: 'linear-gradient(to right, #f8fafc, #fff)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: isComplete ? '#dcfce7' : '#fef3c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CpuIcon sx={{ fontSize: 18, color: isComplete ? '#16a34a' : '#d97706' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                            Agent Execution Feed
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>
                            {isComplete ? 'All Nodes Finalized' : `Active: ${formatNodeName(nodes[nodes.length - 1] || 'Orchestrator')}`}
                        </Typography>
                    </Box>
                </Box>
                {isComplete && (
                    <Chip
                        size="small"
                        label="Complete"
                        sx={{
                            height: 20,
                            fontSize: 9,
                            fontWeight: 700,
                            bgcolor: '#dcfce7',
                            color: '#16a34a',
                            '& .MuiChip-label': { px: 1 },
                        }}
                    />
                )}
            </Box>

            {/* Node Accordions */}
            <Box sx={{ p: 1 }}>
                {nodes.map((node) => {
                    const isExpanded = expandedNodes[node];
                    const nodeTraces = groupedTraces[node];
                    const isLatest = node === nodes[nodes.length - 1];

                    return (
                        <Box key={node} sx={{ mb: 0.5 }}>
                            {/* Accordion Header */}
                            <Box
                                onClick={() => toggleNode(node)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    bgcolor: isExpanded ? '#f1f5f9' : 'transparent',
                                    border: isLatest && !isComplete ? '1px solid' : 'none',
                                    borderColor: '#fcd34d',
                                    '&:hover': { bgcolor: '#f8fafc' },
                                    transition: 'all 0.2s',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton size="small" sx={{ p: 0.5, bgcolor: isExpanded ? '#e2e8f0' : '#f1f5f9' }}>
                                        {isExpanded ? (
                                            <ExpandLessIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                        ) : (
                                            <ExpandMoreIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                        )}
                                    </IconButton>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: isExpanded ? '#1e293b' : '#475569' }}>
                                        {formatNodeName(node)}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={`${nodeTraces.length} events`}
                                        sx={{
                                            height: 18,
                                            fontSize: 9,
                                            fontWeight: 700,
                                            bgcolor: '#f1f5f9',
                                            color: '#64748b',
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                </Box>
                                {isLatest && !isComplete && (
                                    <Chip
                                        size="small"
                                        label="ACTIVE"
                                        sx={{
                                            height: 18,
                                            fontSize: 9,
                                            fontWeight: 700,
                                            bgcolor: '#fef3c7',
                                            color: '#92400e',
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Accordion Content */}
                            <Collapse in={isExpanded}>
                                <Box sx={{ px: 2, pb: 1.5, pt: 1 }}>
                                    {nodeTraces.map((trace, tIdx) => {
                                        const shockKey = `${node}-${tIdx}`;
                                        const hasShocks = trace.data?.shocks && trace.data.shocks.length > 0;
                                        const isShockExpanded = expandedShocks[shockKey];

                                        return (
                                            <Box key={tIdx} sx={{ mb: 1.5 }}>
                                                {/* Trace Card */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1.5,
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        bgcolor: 'rgba(248, 250, 252, 0.5)',
                                                        border: '1px solid transparent',
                                                        '&:hover': {
                                                            bgcolor: '#f8fafc',
                                                            borderColor: '#e2e8f0',
                                                        },
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: 1.5,
                                                            bgcolor: '#fff',
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getIcon(trace.event_type, trace.message)}
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                            <Typography sx={{ fontSize: 12, color: '#334155', fontWeight: 500, lineHeight: 1.5 }}>
                                                                {trace.message}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 9, color: '#94a3b8', fontWeight: 500, flexShrink: 0 }}>
                                                                {trace.timestamp ? new Date(trace.timestamp).toLocaleTimeString([], { hour12: false, minute: '2-digit', second: '2-digit' }) : ''}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* Intel Briefing Card for Shocks */}
                                                {hasShocks && (
                                                    <Box sx={{ ml: 5, mt: 1 }}>
                                                        <Box
                                                            onClick={() => toggleShock(shockKey)}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                px: 1.5,
                                                                py: 1,
                                                                background: 'linear-gradient(to right, #eef2ff, #f5f3ff)',
                                                                border: '1px solid #c7d2fe',
                                                                borderRadius: 2,
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    background: 'linear-gradient(to right, #e0e7ff, #ede9fe)',
                                                                },
                                                                transition: 'all 0.2s',
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <DatabaseIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                    Intel Briefing
                                                                </Typography>
                                                                <Chip
                                                                    size="small"
                                                                    label={`${trace.data!.shocks.length} signals`}
                                                                    sx={{
                                                                        height: 16,
                                                                        fontSize: 9,
                                                                        fontWeight: 700,
                                                                        bgcolor: '#c7d2fe',
                                                                        color: '#4338ca',
                                                                        '& .MuiChip-label': { px: 0.5 },
                                                                    }}
                                                                />
                                                            </Box>
                                                            {isShockExpanded ? (
                                                                <ExpandLessIcon sx={{ fontSize: 16, color: '#818cf8' }} />
                                                            ) : (
                                                                <ExpandMoreIcon sx={{ fontSize: 16, color: '#818cf8' }} />
                                                            )}
                                                        </Box>

                                                        <Collapse in={isShockExpanded}>
                                                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                                {trace.data!.shocks.map((shock: string, sIdx: number) => {
                                                                    const regionMatch = shock.match(/^\[(GULF|LOS_ANGELES|CHICAGO|NATIONAL)[^\]]*\]/);
                                                                    const region = regionMatch ? regionMatch[1] : null;
                                                                    const content = (region && regionMatch) ? shock.replace(regionMatch[0], '').trim() : shock;
                                                                    const regionConfig = region ? REGION_CONFIG[region] : null;
                                                                    const severity = getSeverity(content);
                                                                    const severityColor = SEVERITY_COLORS[severity];

                                                                    return (
                                                                        <Box
                                                                            key={sIdx}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'flex-start',
                                                                                gap: 1,
                                                                                p: 1,
                                                                                mb: sIdx < trace.data!.shocks.length - 1 ? 1 : 0,
                                                                                borderRadius: 1,
                                                                                borderLeft: '3px solid',
                                                                                borderLeftColor: severityColor.border,
                                                                                bgcolor: severityColor.bg,
                                                                            }}
                                                                        >
                                                                            {regionConfig && (
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={region!.replace('_', ' ')}
                                                                                    sx={{
                                                                                        height: 18,
                                                                                        fontSize: 8,
                                                                                        fontWeight: 700,
                                                                                        bgcolor: regionConfig.bg,
                                                                                        color: regionConfig.color,
                                                                                        border: '1px solid',
                                                                                        borderColor: regionConfig.borderColor,
                                                                                        textTransform: 'uppercase',
                                                                                        '& .MuiChip-label': { px: 0.5 },
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            <Typography sx={{ fontSize: 11, color: '#475569', lineHeight: 1.5, flexGrow: 1 }}>
                                                                                {content}
                                                                            </Typography>
                                                                            {severity === 'critical' && <AlertTriangleIcon sx={{ fontSize: 14, color: '#f87171', flexShrink: 0, mt: 0.25 }} />}
                                                                            {severity === 'warning' && <AlertCircleIcon sx={{ fontSize: 14, color: '#fbbf24', flexShrink: 0, mt: 0.25 }} />}
                                                                            {severity === 'info' && <InfoIcon sx={{ fontSize: 14, color: '#cbd5e1', flexShrink: 0, mt: 0.25 }} />}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Collapse>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};
